import { useEffect, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import { CURRENCY, todayIso, type GlTrialBalanceResponse } from "@/lib/gl";

function fmt(n: number) {
  if (Math.abs(n) < 0.005) return "—";
  return `${CURRENCY} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function csvEscape(s: string) {
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function GeneralLedgerTrialBalance() {
  const [asOf, setAsOf] = useState(todayIso());
  const [data, setData] = useState<GlTrialBalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Paginate the account rows only; the Totals + Balanced summary rows below
  // are computed from the full data set and stay visible on every page.
  const pg = usePagination(data?.rows ?? []);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<GlTrialBalanceResponse>(
        `/gl/reports/trial-balance?as_of=${asOf}`,
      );
      setData(d);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load trial balance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    if (!data) return;
    const header = ["Code", "Name", "Type", "Debit", "Credit"];
    const rows = data.rows.map((r) => [
      r.code,
      r.name,
      r.type,
      r.debit.toFixed(2),
      r.credit.toFixed(2),
    ]);
    rows.push(["", "", "Total", data.total_debit.toFixed(2), data.total_credit.toFixed(2)]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => csvEscape(String(v))).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trial-balance-${asOf}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">Trial Balance</CardTitle>
            <CardDescription>
              Totals of all posted journal lines up to the as-of date.
            </CardDescription>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label htmlFor="asof">As of</Label>
              <Input
                id="asof"
                type="date"
                value={asOf}
                max={todayIso()}
                onChange={(e) => setAsOf(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!data}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : !data || data.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    No posted journals to summarise.
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {pg.pageItems.map((r) => (
                    <TableRow key={r.account_id}>
                      <TableCell className="font-mono">{r.code}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell className="capitalize text-muted-foreground">
                        {r.type}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {fmt(r.debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {fmt(r.credit)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/40 font-semibold">
                    <TableCell colSpan={3} className="text-right">
                      Totals
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt(data.total_debit)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt(data.total_credit)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      {data.balanced ? (
                        <Badge
                          variant="outline"
                          className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                        >
                          Balanced
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
                        >
                          Out of balance
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
          {!loading && data && data.rows.length > 0 ? (
            <Pagination
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              from={pg.from}
              to={pg.to}
              onPrev={pg.prev}
              onNext={pg.next}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
